import { useCallback, useEffect, useRef, useState } from "react";
import {
  RagDocument,
  fetchDocuments,
  uploadDocument,
  deleteDocument as apiDeleteDocument,
  retryDocument as apiRetryDocument,
  unassignDocumentFromAgent as apiUnassign,
} from "../api/ragApi";

const POLL_INTERVAL = 5000;

export function useRagDocuments(agentId: number | null) {
  const [documents, setDocuments] = useState<RagDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    if (!agentId) return;
    try {
      setLoading(true);
      const docs = await fetchDocuments(agentId);
      setDocuments(docs);
    } catch (err) {
      console.error("Failed to load documents:", err);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  // Initial load
  useEffect(() => {
    load();
  }, [load]);

  // Poll while any document is processing
  useEffect(() => {
    const hasProcessing = documents.some((d) => d.status === "processing");

    if (hasProcessing && agentId) {
      pollRef.current = setInterval(async () => {
        try {
          const docs = await fetchDocuments(agentId);
          setDocuments(docs);
          // Stop polling if none are processing anymore
          if (!docs.some((d) => d.status === "processing") && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        } catch {
          // Silently fail polling
        }
      }, POLL_INTERVAL);
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [documents, agentId]);

  const tempIdRef = useRef(-1);

  const upload = useCallback(
    async (file: File) => {
      if (!agentId) return;
      // Optimistic: show placeholder immediately
      const tempId = tempIdRef.current--;
      const placeholder: RagDocument = {
        id: tempId,
        agent_id: agentId,
        organization_id: 0,
        uploaded_by: 0,
        filename: file.name,
        s3_key: "",
        mime_type: file.type,
        size_bytes: file.size,
        chunk_count: 0,
        total_chunks_expected: 0,
        status: "processing",
        error_message: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        agents: [],
      };
      setDocuments((prev) => [placeholder, ...prev]);
      setUploading(true);

      try {
        const doc = await uploadDocument(agentId, file);
        setDocuments((prev) => prev.map((d) => (d.id === tempId ? doc : d)));
      } catch (err) {
        setDocuments((prev) => prev.filter((d) => d.id !== tempId));
        console.error("Failed to upload document:", err);
        throw err;
      } finally {
        setUploading(false);
      }
    },
    [agentId],
  );

  const retryDoc = useCallback(async (docId: number) => {
    try {
      const doc = await apiRetryDocument(docId);
      setDocuments((prev) => prev.map((d) => (d.id === docId ? doc : d)));
    } catch (err) {
      console.error("Failed to retry document:", err);
      throw err;
    }
  }, []);

  const deleteDoc = useCallback(async (docId: number) => {
    try {
      await apiDeleteDocument(docId);
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
    } catch (err) {
      console.error("Failed to delete document:", err);
      throw err;
    }
  }, []);

  const unassignDoc = useCallback(
    async (docId: number) => {
      if (!agentId) return;
      try {
        await apiUnassign(docId, agentId);
        setDocuments((prev) => prev.filter((d) => d.id !== docId));
      } catch (err) {
        console.error("Failed to unassign document:", err);
        throw err;
      }
    },
    [agentId],
  );

  return {
    documents,
    loading,
    uploading,
    upload,
    deleteDoc,
    retryDoc,
    unassignDoc,
    reload: load,
  };
}
